/**
 * useInformationBlockAbandonCleanup - best-effort R2 cleanup for abandoned
 * information-block image uploads.
 *
 * An information-block image is uploaded to R2 the moment the author drops it,
 * before the campaign is saved. If the author then navigates away or closes the
 * tab without publishing, those objects are orphaned. This hook deletes every
 * still-uncommitted image on two abandonment paths:
 *
 * - SPA route unmount: an async delete that fetches a fresh token per object.
 * - `beforeunload`: a synchronous `keepalive` fetch per object. The unload
 *   handler cannot await `getSession`, so the token is pre-fetched into a ref
 *   via `onAuthStateChange`. `keepalive` (not `sendBeacon`) is required because
 *   the delete endpoint needs an `Authorization` header, which `sendBeacon`
 *   cannot set.
 *
 * `markSaved()` opts a published wizard out of BOTH paths: once the campaign is
 * saved, its images are committed and must not be deleted.
 *
 * @module components/form-builder/useInformationBlockAbandonCleanup
 */

import { useCallback, useEffect, useRef } from 'react';
import { useSupabase } from '../../lib/SupabaseProvider';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { offlineSessionWorkerUrl } from '../../lib/worker-urls';
import type { ConversationSection } from '../../lib/conversation-sections';
import type { FormField } from '../../types';

/** Collect every uploaded image owned by authored content or a visual question. */
export function collectQuestionImageStorageKeys(fields: readonly FormField[], sections?: readonly ConversationSection[]): string[] {
  const keys = new Set<string>();
  const sourceFields = sections?.flatMap(section => section.blocks?.flatMap(block => block.sourceFields) ?? []) ?? [];
  for (const field of [...fields, ...sourceFields]) {
    if (field.type === 'information' && field.imageStorageKey) keys.add(field.imageStorageKey);
    if (field.type === 'image_choice') {
      for (const option of field.options) if (option.image?.imageStorageKey) keys.add(option.image.imageStorageKey);
    }
    if (field.type === 'image_click_map' && field.image?.imageStorageKey) keys.add(field.image.imageStorageKey);
  }
  return [...keys];
}

/** Options for {@link useInformationBlockAbandonCleanup}. */
export interface UseInformationBlockAbandonCleanupOptions {
  /** Owning workspace id, sent in every delete call. */
  workspaceId?: string;
  /**
   * Getter for the currently-uncommitted info-block storage keys. Called at
   * cleanup time (unmount / unload) so it reflects the live field set rather
   * than a stale snapshot from render time.
   */
  getStorageKeys: () => string[];
}

/** Return shape of {@link useInformationBlockAbandonCleanup}. */
export interface UseInformationBlockAbandonCleanupResult {
  /**
   * Mark the wizard as saved. Disables both the unmount and the `beforeunload`
   * cleanup paths so a successful publish does not delete its own images.
   */
  markSaved: () => void;
}

/**
 * Wire abandonment cleanup for the current set of uncommitted info-block keys.
 *
 * @param options See {@link UseInformationBlockAbandonCleanupOptions}.
 * @returns `{ markSaved }` to short-circuit cleanup on a successful save.
 */
export function useInformationBlockAbandonCleanup(
  options: UseInformationBlockAbandonCleanupOptions,
): UseInformationBlockAbandonCleanupResult {
  const { workspaceId, getStorageKeys } = options;
  const { supabase } = useSupabase();

  const savedRef = useRef(false);
  const tokenRef = useRef<string | null>(null);
  const workspaceIdRef = useRef(workspaceId);
  workspaceIdRef.current = workspaceId;
  const getKeysRef = useRef(getStorageKeys);
  getKeysRef.current = getStorageKeys;

  // Pre-fetch the access token into a ref. The `beforeunload` handler runs
  // synchronously and cannot await `getSession`, so we keep the latest token
  // hot via `onAuthStateChange`.
  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      tokenRef.current = session?.access_token ?? null;
    });
    return () => data.subscription.unsubscribe();
  }, [supabase]);

  // `beforeunload`: synchronous keepalive delete per uncommitted key.
  useEffect(() => {
    const onBeforeUnload = () => {
      if (savedRef.current) return;
      const token = tokenRef.current;
      if (!token) return;
      const keys = getKeysRef.current();
      const base = offlineSessionWorkerUrl();
      for (const key of keys) {
        try {
          void fetch(`${base}/information-block/delete`, {
            method: 'POST',
            keepalive: true,
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              workspaceId: workspaceIdRef.current,
              imageStorageKey: key,
            }),
          }).then(response => {
            if (!response.ok) throw new Error(`Image deletion failed (${response.status})`);
          }).catch(err => {
            captureException(
              err instanceof Error ? err : new Error(String(err)),
              'useInformationBlockAbandonCleanup.beforeunload',
              { workspaceId: workspaceIdRef.current },
            );
          });
        } catch (err) {
          captureException(
            err instanceof Error ? err : new Error(String(err)),
            'useInformationBlockAbandonCleanup.beforeunload',
            { workspaceId: workspaceIdRef.current },
          );
        }
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  // SPA route unmount: async delete with a freshly fetched token. This is the
  // cleanup effect's teardown; it runs once when the component unmounts.
  useEffect(
    () => () => {
      if (savedRef.current) return;
      const keys = getKeysRef.current();
      if (keys.length === 0) return;
      const ws = workspaceIdRef.current;
      const base = offlineSessionWorkerUrl();
      void (async () => {
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          const token = session?.access_token;
          if (!token) return;
          await Promise.all(
            keys.map(async (key) => {
              const response = await fetch(`${base}/information-block/delete`, {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ workspaceId: ws, imageStorageKey: key }),
              });
              if (!response.ok) throw new Error(`Image deletion failed (${response.status})`);
            }),
          );
        } catch (err) {
          captureException(
            err instanceof Error ? err : new Error(String(err)),
            'useInformationBlockAbandonCleanup.unmount',
            { workspaceId: ws },
          );
        }
      })();
    },
    [supabase],
  );

  const markSaved = useCallback(() => {
    savedRef.current = true;
  }, []);

  return { markSaved };
}
